import dynamic from 'next/dynamic';
import { useTranslation } from 'next-i18next/pages';
import { type TFunction } from '@pkgs/components/providers';
import { UserAccountSignInDialog } from '@pkgs/components/components/UserAccountModal/UserAccountSignInDialog';
import { Wrapper } from '@ev-tech/ui-core-components/Wrapper';

import { PageVariant } from '@/types/expose';
import { TRANSLATION_NAMESPACE } from '@/i18n/config';
import { useShouldHideUserAccountExclusiveContent } from '@/hooks/useShouldHideUserAccountExclusiveContent';
import { useSettings } from '@/hooks/useSettings';
import { useListingPageComponentRefs } from '@/hooks/useListingPageComponentRefs';
import { useExposePageProps } from '@/hooks/useExposePageProps';
import { useListing } from '@/hooks/queries/useListing';
import { TransComponent } from '@/components/common/TransComponent';

import { ContactForm } from './ContactForm';
import { PrivateOfficePasswordForm } from '../PrivateOfficePasswordForm/PrivateOfficePasswordForm';
import { Intro } from '../Intro/Intro';

import styles from './ContactFormSection.module.css';

const AgentShopCard = dynamic(() => import('../AgentShopCard/AgentShopCard').then((module) => ({ default: module.AgentShopCard })));
const RequestAccessForm = dynamic(() =>
  import('../RequestAccessForm/RequestAccessForm').then((module) => ({ default: module.RequestAccessForm })),
);

export const ContactFormSection = () => {
  const { contactFormCtaRef, contactFormInputGridRef } = useListingPageComponentRefs();
  const { pageVariant, pageTrackingTypes } = useExposePageProps();
  const { countryCode, language } = useSettings();
  const isUAExclusiveListingAndNotSignedIn = useShouldHideUserAccountExclusiveContent();
  const { t } = useTranslation(TRANSLATION_NAMESPACE.user_account);
  const {
    data: { listing, restrictedListing },
  } = useListing();

  if (isUAExclusiveListingAndNotSignedIn) {
    return (
      <section className={styles.signInDialogContainer}>
        <Wrapper className={styles.wrapper} width={{ laptop: 'l', tabletLandscape: 'l' }}>
          <div className={styles.signInDialogContent}>
            <UserAccountSignInDialog
              tFunc={t as TFunction}
              transComponent={TransComponent}
              className={styles.loginSideBar}
              countryCode={countryCode}
              language={language}
              tracking={pageTrackingTypes}
            />
          </div>
        </Wrapper>
      </section>
    );
  }

  if (restrictedListing) {
    return (
      <section data-testid="expose-contact-form">
        <Wrapper className={styles.restrictedFormBoxWrapper} width={{ laptop: 'l', tabletLandscape: 'l' }}>
          <div className={styles.restrictedFormBox}>
            {pageVariant === PageVariant.PRIVATE_OFFICE_RESTRICTED_WITH_PASSWORD_PROTECTION ? (
              <PrivateOfficePasswordForm isEmbedded />
            ) : (
              <RequestAccessForm restrictedListing={restrictedListing} />
            )}
            <div className={styles.agentShopCardWrapper}>
              <AgentShopCard isEmbedded />
            </div>
          </div>
        </Wrapper>
      </section>
    );
  }

  if (!listing) {
    return null;
  }

  return (
    <section data-testid="expose-contact-form">
      <Wrapper className={styles.wrapper} width={{ laptop: 'l', tabletLandscape: 'l' }}>
        <Intro className={styles.intro} headline={{ label: '' }} verticalLineTop={true} nested={true} />
        <div className={styles.formBox}>
          <ContactForm buttonRef={contactFormCtaRef} inputGridRef={contactFormInputGridRef} />
        </div>
      </Wrapper>
    </section>
  );
};
